import { useEffect, useState } from 'react';
import { db } from '../firebase';
import {
  collection,
  getDocs,
  doc,
  updateDoc,
} from 'firebase/firestore';
import './PlaceLibrary.css';

function PlaceLibrary({ user }) {
  const [places, setPlaces] = useState([]);
  const [filteredPlaces, setFilteredPlaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter and sort state
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [columnFilters, setColumnFilters] = useState({});
  const [visibleFilters, setVisibleFilters] = useState({});
  const [distanceFilters, setDistanceFilters] = useState({});
  const [showIgnored, setShowIgnored] = useState(false);
  
  // Edit state
  const [editingComment, setEditingComment] = useState(null);
  const [commentText, setCommentText] = useState('');

  useEffect(() => {
    if (user) {
      loadPlaces();
    }
  }, [user]);

  useEffect(() => {
    applyFiltersAndSort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [places, searchTerm, sortField, sortDirection, columnFilters, showIgnored]);

  const loadPlaces = async () => {
    setLoading(true);
    setError(null);
    try {
      const querySnapshot = await getDocs(collection(db, 'place_details'));
      const placesData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setPlaces(placesData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 3959; // Earth's radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const applyFiltersAndSort = () => {
    let result = [...places].map((place) => ({
      ...place,
      distanceToChelmsford: place.lat && place.lon 
        ? calculateDistance(42.612758, -71.351658, parseFloat(place.lat), parseFloat(place.lon))
        : null,
      distanceToMansfield: place.lat && place.lon
        ? calculateDistance(42.052129, -71.190138, parseFloat(place.lat), parseFloat(place.lon))
        : null,
    }));

    // Filter by ignored status and favorites
    if (showIgnored) {
      result = result.filter((place) => place.isIgnored === true);
    } else {
      // Exclude both ignored and favorited entries from library
      result = result.filter((place) => !place.isIgnored && !place.isFavorite);
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (place) =>
          place.name?.toLowerCase().includes(term) ||
          place.formatted_address?.toLowerCase().includes(term) ||
          place.types?.toLowerCase().includes(term)
      );
    }

    // Apply column filters
    Object.keys(columnFilters).forEach((column) => {
      const filterValue = columnFilters[column];
      if (filterValue) {
        const term = filterValue.toLowerCase();
        result = result.filter((place) => {
          const value = place[column];
          return value && String(value).toLowerCase().includes(term);
        });
      }
    });

    // Apply distance filters
    Object.keys(distanceFilters).forEach((column) => {
      const filter = distanceFilters[column];
      if (filter && filter.type) {
        result = result.filter((place) => {
          const distance = place[column];
          if (distance === null || distance === undefined) return false;
          
          if (filter.type === 'greater') {
            return distance > parseFloat(filter.value || 0);
          } else if (filter.type === 'less') {
            return distance < parseFloat(filter.value || 999999);
          } else if (filter.type === 'between') {
            const min = parseFloat(filter.min || 0);
            const max = parseFloat(filter.max || 999999);
            return distance >= min && distance <= max;
          }
          return true;
        });
      }
    });

    // Sort
    result.sort((a, b) => {
      let aVal = a[sortField] ?? '';
      let bVal = b[sortField] ?? '';

      // Handle numeric fields
      if (sortField === 'rating' || sortField === 'user_ratings_total' || 
          sortField === 'distanceToChelmsford' || sortField === 'distanceToMansfield') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else {
        aVal = String(aVal).toLowerCase();
        bVal = String(bVal).toLowerCase();
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredPlaces(result);
  };

  const handleColumnFilter = (column, value) => {
    setColumnFilters((prev) => ({
      ...prev,
      [column]: value,
    }));
  };

  const toggleFilterVisibility = (column) => {
    setVisibleFilters((prev) => ({
      ...prev,
      [column]: !prev[column],
    }));
  };

  const handleDistanceFilter = (column, type, value, min, max) => {
    setDistanceFilters((prev) => ({
      ...prev,
      [column]: { type, value, min, max },
    }));
  };

  const toggleFavorite = async (placeId, currentStatus) => {
    try {
      const placeRef = doc(db, 'place_details', placeId);
      await updateDoc(placeRef, {
        isFavorite: !currentStatus,
      });
      setPlaces((prev) =>
        prev.map((p) =>
          p.id === placeId ? { ...p, isFavorite: !currentStatus } : p
        )
      );
    } catch (err) {
      setError(err.message);
    }
  };

  const toggleIgnore = async (placeId, currentStatus) => {
    try {
      const placeRef = doc(db, 'place_details', placeId);
      await updateDoc(placeRef, {
        isIgnored: !currentStatus,
      });
      setPlaces((prev) =>
        prev.map((p) =>
          p.id === placeId ? { ...p, isIgnored: !currentStatus } : p
        )
      );
    } catch (err) {
      setError(err.message);
    }
  };

  const saveComment = async (placeId) => {
    try {
      const placeRef = doc(db, 'place_details', placeId);
      await updateDoc(placeRef, {
        comment: commentText,
      });
      setPlaces((prev) =>
        prev.map((p) =>
          p.id === placeId ? { ...p, comment: commentText } : p
        )
      );
      setEditingComment(null);
      setCommentText('');
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  if (!user) {
    return (
      <div className="library-container">
        <p>Please sign in to view the place library.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="library-container">
        <p>Loading places...</p>
      </div>
    );
  }

  const extractTown = (address) => {
    if (!address) return '—';
    // Extract town from address format: "street, town, state zip, country"
    const parts = address.split(',');
    if (parts.length >= 2) {
      return parts[1].trim();
    }
    return address;
  };

  return (
    <div className="library-container">
      <div className="header-row">
        <h2>Library</h2>
        <div className="header-controls">
          <button
            className={`toggle-ignored-btn ${showIgnored ? 'active' : ''}`}
            onClick={() => setShowIgnored(!showIgnored)}
            title={showIgnored ? 'Show active entries' : 'Show ignored entries'}
          >
            {showIgnored ? '👁️ Ignored' : '📋 Active'}
          </button>
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input-compact"
          />
        </div>
      </div>

      {error && (
        <div className="error" role="alert">
          <strong>Error:</strong> {error}
        </div>
      )}

      <div className="table-container">
        <table className="places-table">
          <thead>
            <tr>
              <th>Actions</th>
              <th className="sortable">
                <div className="header-content" onClick={() => handleSort('name')}>
                  Name {sortField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
                  <button
                    className="filter-toggle"
                    onClick={(e) => { e.stopPropagation(); toggleFilterVisibility('name'); }}
                    title="Toggle filter"
                  >
                    🔍
                  </button>
                </div>
                {visibleFilters.name && (
                  <input
                    type="text"
                    placeholder="Filter..."
                    value={columnFilters.name || ''}
                    onChange={(e) => handleColumnFilter('name', e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    className="column-filter"
                  />
                )}
              </th>
              <th className="sortable">
                <div className="header-content" onClick={() => handleSort('formatted_address')}>
                  Address {sortField === 'formatted_address' && (sortDirection === 'asc' ? '↑' : '↓')}
                  <button
                    className="filter-toggle"
                    onClick={(e) => { e.stopPropagation(); toggleFilterVisibility('formatted_address'); }}
                    title="Toggle filter"
                  >
                    🔍
                  </button>
                </div>
                {visibleFilters.formatted_address && (
                  <input
                    type="text"
                    placeholder="Filter..."
                    value={columnFilters.formatted_address || ''}
                    onChange={(e) => handleColumnFilter('formatted_address', e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    className="column-filter"
                  />
                )}
              </th>
              <th className="sortable">
                <div className="header-content" onClick={() => handleSort('formatted_phone_number')}>
                  Phone {sortField === 'formatted_phone_number' && (sortDirection === 'asc' ? '↑' : '↓')}
                  <button
                    className="filter-toggle"
                    onClick={(e) => { e.stopPropagation(); toggleFilterVisibility('formatted_phone_number'); }}
                    title="Toggle filter"
                  >
                    🔍
                  </button>
                </div>
                {visibleFilters.formatted_phone_number && (
                  <input
                    type="text"
                    placeholder="Filter..."
                    value={columnFilters.formatted_phone_number || ''}
                    onChange={(e) => handleColumnFilter('formatted_phone_number', e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    className="column-filter"
                  />
                )}
              </th>
              <th className="sortable">
                <div className="header-content" onClick={() => handleSort('rating')}>
                  Rating {sortField === 'rating' && (sortDirection === 'asc' ? '↑' : '↓')}
                  <button
                    className="filter-toggle"
                    onClick={(e) => { e.stopPropagation(); toggleFilterVisibility('rating'); }}
                    title="Toggle filter"
                  >
                    🔍
                  </button>
                </div>
                {visibleFilters.rating && (
                  <input
                    type="text"
                    placeholder="Filter..."
                    value={columnFilters.rating || ''}
                    onChange={(e) => handleColumnFilter('rating', e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    className="column-filter"
                  />
                )}
              </th>
              <th className="sortable">
                <div className="header-content" onClick={() => handleSort('types')}>
                  Types {sortField === 'types' && (sortDirection === 'asc' ? '↑' : '↓')}
                  <button
                    className="filter-toggle"
                    onClick={(e) => { e.stopPropagation(); toggleFilterVisibility('types'); }}
                    title="Toggle filter"
                  >
                    🔍
                  </button>
                </div>
                {visibleFilters.types && (
                  <input
                    type="text"
                    placeholder="Filter..."
                    value={columnFilters.types || ''}
                    onChange={(e) => handleColumnFilter('types', e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    className="column-filter"
                  />
                )}
              </th>
              <th className="sortable">
                <div className="header-content" onClick={() => handleSort('distanceToChelmsford')}>
                  Chelmsford {sortField === 'distanceToChelmsford' && (sortDirection === 'asc' ? '↑' : '↓')}
                  <button
                    className="filter-toggle"
                    onClick={(e) => { e.stopPropagation(); toggleFilterVisibility('distanceToChelmsford'); }}
                    title="Toggle filter"
                  >
                    🔍
                  </button>
                </div>
                {visibleFilters.distanceToChelmsford && (
                  <div className="distance-filter" onClick={(e) => e.stopPropagation()}>
                    <select
                      value={distanceFilters.distanceToChelmsford?.type || ''}
                      onChange={(e) => handleDistanceFilter('distanceToChelmsford', e.target.value, '', '', '')}
                      className="distance-select"
                    >
                      <option value="">Select...</option>
                      <option value="greater">Greater than</option>
                      <option value="less">Less than</option>
                      <option value="between">Between</option>
                    </select>
                    {distanceFilters.distanceToChelmsford?.type === 'greater' && (
                      <input
                        type="number"
                        placeholder="Miles"
                        value={distanceFilters.distanceToChelmsford?.value || ''}
                        onChange={(e) => handleDistanceFilter('distanceToChelmsford', 'greater', e.target.value, '', '')}
                        className="distance-input"
                      />
                    )}
                    {distanceFilters.distanceToChelmsford?.type === 'less' && (
                      <input
                        type="number"
                        placeholder="Miles"
                        value={distanceFilters.distanceToChelmsford?.value || ''}
                        onChange={(e) => handleDistanceFilter('distanceToChelmsford', 'less', e.target.value, '', '')}
                        className="distance-input"
                      />
                    )}
                    {distanceFilters.distanceToChelmsford?.type === 'between' && (
                      <div className="between-inputs">
                        <input
                          type="number"
                          placeholder="Min"
                          value={distanceFilters.distanceToChelmsford?.min || ''}
                          onChange={(e) => handleDistanceFilter('distanceToChelmsford', 'between', '', e.target.value, distanceFilters.distanceToChelmsford?.max || '')}
                          className="distance-input"
                        />
                        <input
                          type="number"
                          placeholder="Max"
                          value={distanceFilters.distanceToChelmsford?.max || ''}
                          onChange={(e) => handleDistanceFilter('distanceToChelmsford', 'between', '', distanceFilters.distanceToChelmsford?.min || '', e.target.value)}
                          className="distance-input"
                        />
                      </div>
                    )}
                  </div>
                )}
              </th>
              <th className="sortable">
                <div className="header-content" onClick={() => handleSort('distanceToMansfield')}>
                  Mansfield {sortField === 'distanceToMansfield' && (sortDirection === 'asc' ? '↑' : '↓')}
                  <button
                    className="filter-toggle"
                    onClick={(e) => { e.stopPropagation(); toggleFilterVisibility('distanceToMansfield'); }}
                    title="Toggle filter"
                  >
                    🔍
                  </button>
                </div>
                {visibleFilters.distanceToMansfield && (
                  <div className="distance-filter" onClick={(e) => e.stopPropagation()}>
                    <select
                      value={distanceFilters.distanceToMansfield?.type || ''}
                      onChange={(e) => handleDistanceFilter('distanceToMansfield', e.target.value, '', '', '')}
                      className="distance-select"
                    >
                      <option value="">Select...</option>
                      <option value="greater">Greater than</option>
                      <option value="less">Less than</option>
                      <option value="between">Between</option>
                    </select>
                    {distanceFilters.distanceToMansfield?.type === 'greater' && (
                      <input
                        type="number"
                        placeholder="Miles"
                        value={distanceFilters.distanceToMansfield?.value || ''}
                        onChange={(e) => handleDistanceFilter('distanceToMansfield', 'greater', e.target.value, '', '')}
                        className="distance-input"
                      />
                    )}
                    {distanceFilters.distanceToMansfield?.type === 'less' && (
                      <input
                        type="number"
                        placeholder="Miles"
                        value={distanceFilters.distanceToMansfield?.value || ''}
                        onChange={(e) => handleDistanceFilter('distanceToMansfield', 'less', e.target.value, '', '')}
                        className="distance-input"
                      />
                    )}
                    {distanceFilters.distanceToMansfield?.type === 'between' && (
                      <div className="between-inputs">
                        <input
                          type="number"
                          placeholder="Min"
                          value={distanceFilters.distanceToMansfield?.min || ''}
                          onChange={(e) => handleDistanceFilter('distanceToMansfield', 'between', '', e.target.value, distanceFilters.distanceToMansfield?.max || '')}
                          className="distance-input"
                        />
                        <input
                          type="number"
                          placeholder="Max"
                          value={distanceFilters.distanceToMansfield?.max || ''}
                          onChange={(e) => handleDistanceFilter('distanceToMansfield', 'between', '', distanceFilters.distanceToMansfield?.min || '', e.target.value)}
                          className="distance-input"
                        />
                      </div>
                    )}
                  </div>
                )}
              </th>
              <th>Website</th>
              <th>Comment</th>
            </tr>
          </thead>
          <tbody>
            {filteredPlaces.map((place) => (
              <tr key={place.id}>
                <td>
                  <div className="action-buttons">
                    <button
                      className={`favorite-btn ${place.isFavorite ? 'active' : ''}`}
                      onClick={() => toggleFavorite(place.id, place.isFavorite)}
                      title={place.isFavorite ? 'Remove from targets' : 'Add to targets'}
                    >
                      {place.isFavorite ? '★' : '☆'}
                    </button>
                    <button
                      className={`ignore-btn ${place.isIgnored ? 'active' : ''}`}
                      onClick={() => toggleIgnore(place.id, place.isIgnored)}
                      title={place.isIgnored ? 'Unignore' : 'Ignore'}
                    >
                      {place.isIgnored ? '🚫' : '⊘'}
                    </button>
                  </div>
                </td>
                <td>{place.name || '—'}</td>
                <td className="address-cell" title={place.formatted_address || ''}>
                  {extractTown(place.formatted_address)}
                </td>
                <td className="phone-cell">
                  {place.formatted_phone_number ? (
                    <span className="phone-icon" title={place.formatted_phone_number}>
                      📞
                    </span>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="rating-cell">
                  {place.rating ? (
                    <span>
                      ⭐ {place.rating}
                      {place.user_ratings_total && (
                        <span className="review-count"> ({place.user_ratings_total})</span>
                      )}
                    </span>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="types-cell" title={place.types ? place.types.replace(/\|/g, ', ') : ''}>
                  {place.types ? place.types.replace(/\|/g, ', ') : '—'}
                </td>
                <td>{place.distanceToChelmsford ? `${place.distanceToChelmsford.toFixed(1)} mi` : '—'}</td>
                <td>{place.distanceToMansfield ? `${place.distanceToMansfield.toFixed(1)} mi` : '—'}</td>
                <td>
                  {place.website ? (
                    <a href={place.website} target="_blank" rel="noopener noreferrer">
                      Visit
                    </a>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="comment-cell">
                  {editingComment === place.id ? (
                    <div className="comment-edit">
                      <textarea
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                        placeholder="Add a comment..."
                        rows={2}
                      />
                      <div className="comment-actions">
                        <button onClick={() => saveComment(place.id)}>Save</button>
                        <button onClick={() => setEditingComment(null)}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="comment-display"
                      onClick={() => {
                        setEditingComment(place.id);
                        setCommentText(place.comment || '');
                      }}
                    >
                      {place.comment || 'Click to add comment'}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="results-count">
        Showing {filteredPlaces.length} of {places.length} places
      </div>
    </div>
  );
}

export default PlaceLibrary;
